import { useRef, useState } from 'react';
import { Button, FormGroup, InputGroup, InputGroupText } from 'reactstrap';
import { preCache, snapdom } from '@zumer/snapdom';
import { jsPDF } from 'jspdf';
import i18n from '@/utils/i18n';
import { ContainerTypeLabel } from './ContainerTypeLabel';
import { getMissingFieldsForLabel } from './containerTypeLabelUtils';
import Modal from '../../components/Modal';
import Checkbox from '../../components/Checkbox';
import { download } from '@/utils/download';

import type { ContainerType } from '../ContainerComponents/ContainerPage';
import Loading from '../../components/Loading';

type ContainerTypeLabelGenerationModalProps = {
  containerType: ContainerType;
  onClose: () => void;
};

export function ContainerTypeLabelGenerationModal({
  containerType,
  onClose,
}: ContainerTypeLabelGenerationModalProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [isSmallLabel, setIsSmallLabel] = useState(false);
  const [hasRfid, setHasRfid] = useState(false);

  const [isReady, setIsReady] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const dateStamp = new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const missingFields = getMissingFieldsForLabel(containerType);

  const getSnapdomScale = () => {
    if (!containerRef.current) return 1;
    const containerSize = containerRef.current.getBoundingClientRect();
    // 2412px height is around 600dpi for 154mm x 102mm
    return containerSize.height > 2412
      ? 1
      : Math.floor(2412 / containerSize.height);
  };

  const handleExport = async () => {
    if (!containerRef.current) return;

    setIsExporting(true);

    await snapdom.download(containerRef.current, {
      scale: getSnapdomScale(),
      dpr: 2,
      type: 'png',
      fast: false,
      filename: `container_type_${containerType.id}_label_${dateStamp}.png`,
      embedFonts: true,
    });

    setIsExporting(false);
  };

  const handleExportPdf = async () => {
    if (!pdfContainerRef.current) return;

    setIsExporting(true);

    const pdfSize = pdfContainerRef.current.getBoundingClientRect();
    // ~600 DPI for Avery 5164 labels (84.67mm ≈ 3.33")
    const pdfScale =
      pdfSize.height > 2000 ? 1 : Math.floor(2000 / pdfSize.height);

    const blob = await snapdom.toBlob(pdfContainerRef.current, {
      scale: pdfScale,
      dpr: 1,
      type: 'png',
      fast: false,
      embedFonts: true,
    });

    const imageUrl = URL.createObjectURL(blob);

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 2; col++) {
        const x = LEFT_MARGIN_PT + col * (LABEL_WIDTH_PT + H_GAP_PT);
        const y = TOP_MARGIN_PT + row * LABEL_HEIGHT_PT;
        doc.addImage(imageUrl, 'PNG', x, y, LABEL_WIDTH_PT, LABEL_HEIGHT_PT);
      }
    }

    const pdfBlob = doc.output('blob');
    URL.revokeObjectURL(imageUrl);
    download(
      pdfBlob,
      `container_type_${containerType.id}_labels_${dateStamp}.pdf`,
    );

    setIsExporting(false);
  };

  const handleReady = () => {
    setTimeout(() => {
      setIsReady(true);
      if (containerRef.current) {
        preCache(containerRef.current, {
          embedFonts: true,
        });
      }
      if (pdfContainerRef.current) {
        preCache(pdfContainerRef.current, {
          embedFonts: true,
        });
      }
    }, 200);
  };

  return (
    <Modal onClose={onClose}>
      <div style={styles.modalContent}>
        <div style={{ position: 'relative' }}>
          <div ref={containerRef}>
            <ContainerTypeLabel
              containerType={containerType}
              dateStamp={dateStamp}
              size={isSmallLabel ? 'small' : 'normal'}
              hasRfid={hasRfid}
              scale={4}
              onReady={handleReady}
            />
          </div>
          {!isReady && (
            <div style={styles.loadingContainer}>
              <Loading />
            </div>
          )}
        </div>
        <div
          ref={pdfContainerRef}
          style={{ position: 'absolute', left: -9999, top: -9999 }}
        >
          <ContainerTypeLabel
            containerType={containerType}
            dateStamp={dateStamp}
            size="avery5164"
            hasRfid={hasRfid}
            scale={4}
          />
        </div>
        {missingFields.length === 0 && (
          <>
            <FormGroup
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: 16,
                paddingLeft: 72,
                paddingRight: 72,
              }}
            >
              <InputGroup
                onClick={() => {
                  setIsSmallLabel((p) => !p);
                }}
                style={{ cursor: 'pointer', width: 'auto', flex: 'none' }}
              >
                <InputGroupText>Small label (Cutlery box etc.)</InputGroupText>
                <InputGroupText>
                  <Checkbox checked={isSmallLabel} />
                </InputGroupText>
              </InputGroup>
              <InputGroup
                onClick={() => {
                  setHasRfid((p) => !p);
                }}
                style={{ cursor: 'pointer', width: 'auto', flex: 'none' }}
              >
                <InputGroupText>RFID</InputGroupText>
                <InputGroupText>
                  <Checkbox checked={hasRfid} />
                </InputGroupText>
              </InputGroup>
            </FormGroup>
            <div style={{ display: 'flex', gap: 8, marginTop: -16 }}>
              <Button
                color="primary"
                onClick={handleExport}
                disabled={isExporting}
              >
                Download PNG
              </Button>
              <Button
                color="primary"
                onClick={handleExportPdf}
                disabled={isExporting}
              >
                Download PDF (Avery 5164)
              </Button>
            </div>
          </>
        )}
        {missingFields.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button onClick={onClose}>{i18n.t('Common:close')}</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// Avery 5164: US Letter (612x792pt), 2 cols x 3 rows
// Extracted from template PDF: each label 288x240pt, gap 13.549pt, margins ~11.2pt/36pt
const LABEL_WIDTH_PT = 288;
const LABEL_HEIGHT_PT = 240;
const H_GAP_PT = 13.549;
const LEFT_MARGIN_PT = 11.201;
const TOP_MARGIN_PT = 36;

const styles = {
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    backgroundColor: '#F0F0F0',
    padding: 16,
    borderRadius: 16,
    overflow: 'hidden',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
} as const;
